import { getDestinationPath } from '@utils/getDestinationPath';
import getDonateWidgetMode from '@utils/getDonateWidgetMode';
import {
  getLangFromPath,
  isLocalizedPath,
  localizedPath,
} from '@utils/localized-path';

import { WIDGET_DESTINATION_PATHS } from '@constants/index';

import { LoadReactApp } from '@typings/widget';

import { log } from '@hooks/logger';

import { LogLevel } from '@typings/operations';

/**
 * Here below is all the logic covers Donate Widget integration onto page
 *
 * To be called in 'gatsby-browser.js'
 */
export const waitForDonateWidget = (
  selector: string,
  checkFrequencyInMs: number,
  timeoutInMs: number
) => {
  log({
    level: LogLevel.Debug,
    message: `waitForDonateWidget execution started`,
  });
  const { destination } = getDestinationPath(WIDGET_DESTINATION_PATHS);

  log({
    level: LogLevel.Debug,
    message: `waitForDonateWidget destination  ${destination}`,
  });

  const existedScript: HTMLScriptElement | null = document.querySelector(
    'script[src*="loadData.js"]'
  );

  log({
    level: LogLevel.Debug,
    message: `waitForDonateWidget existedScript ${existedScript}`,
  });

  const doanteWidgetURL = process.env.GATSBY_DONATE_WIDGET_URL as string;
  log({
    level: LogLevel.Debug,
    message: `waitForDonateWidget doanteWidgetURL ${doanteWidgetURL}`,
  });
  const donateWidgetLoadBuildJS = process.env
    .GATSBY_DONATE_WIDGET_LOAD_BUILD_JS as string;

  log({
    level: LogLevel.Debug,
    message: `waitForDonateWidget donateWidgetLoadBuildJS ${donateWidgetLoadBuildJS}`,
  });
  const script: HTMLScriptElement =
    existedScript || document.createElement('script');

  log({
    level: LogLevel.Debug,
    message: `waitForDonateWidget script ${script}`,
  });

  if (!existedScript) {
    script.src = donateWidgetLoadBuildJS;
    script.type = 'text/javascript';
    document.head.prepend(script);
  }

  const interval = setInterval(() => {
    const element = document.querySelector(selector);
    const elementContainer = document.getElementById('donate-widget');

    log({
      level: LogLevel.Debug,
      message: `waitForDonateWidget element ${element} elementContainer ${elementContainer}`,
    });

    const user = window.__gatsby_user;
    const gatsbyLanguage = window.__gatsby_language;

    const { pathname, origin } = window.location;

    log({
      level: LogLevel.Debug,
      message: `waitForDonateWidget user ${user} gatsbyLanguage ${gatsbyLanguage} pathname ${pathname} ${origin}`,
    });

    log({
      level: LogLevel.Debug,
      message: `waitForDonateWidget isLocalizedPath(pathname)  ${isLocalizedPath(
        pathname
      )} getLangFromPath(pathname) ${getLangFromPath(pathname)}`,
    });

    const donateWidgetObject =
      getDonateWidgetMode(destination) === 'invoice'
        ? {
            invoice: JSON.parse(
              window.localStorage.getItem('serverValue') as string
            ),
            getLanguage: gatsbyLanguage,
            mode: getDonateWidgetMode(destination),
          }
        : {
            getLanguage: gatsbyLanguage,
            mode: getDonateWidgetMode(destination),
          };

    const donateWidget = () =>
      (window.loadReactApp as LoadReactApp)(
        doanteWidgetURL,
        donateWidgetObject,
        document.getElementById('donate-widget')
      );

    // CASE 1
    // Breaks interval loop if donateWidget and element are loaded on the page.
    // This means no other activities is needed.
    if (window.loadReactApp && element !== null) {
      log({
        level: LogLevel.Debug,
        message: `waitForDonateWidget CASE 1 interval ${interval}`,
      });
      clearInterval(interval);
    }
    // CASE 2
    // Makes localized navigation or fires widget with params
    else if (
      window.loadReactApp &&
      element === null &&
      elementContainer !== null
    ) {
      log({
        level: LogLevel.Debug,
        message: `waitForDonateWidget CASE 2 window.loadReactApp ${window.loadReactApp}`,
      });
      // CASE 2.1
      // If current path is not localized then add
      // localization and navigate to localized path.
      if (
        !isLocalizedPath(pathname) &&
        gatsbyLanguage !== getLangFromPath(pathname)
      ) {
        log({
          level: LogLevel.Debug,
          message: `waitForDonateWidget CASE 2.1 href path ${origin +
            localizedPath(pathname, getLangFromPath(pathname) || '')}`,
        });
        clearInterval(interval);
        window.location.href =
          origin + localizedPath(pathname, getLangFromPath(pathname) || '');
      }
      // CASE 2.2
      // Make delayed donateWidget call
      else if (
        (user &&
          isLocalizedPath(pathname) &&
          gatsbyLanguage === getLangFromPath(pathname)) ||
        (!user && gatsbyLanguage === getLangFromPath(pathname))
      ) {
        log({
          level: LogLevel.Debug,
          message: `waitForDonateWidget CASE 2.2 interval ${interval}`,
        });
        clearInterval(interval);
        setTimeout(() => donateWidget(), 2000);
      }
    }
    // CASE 3
    // Breaks interval loop after timeout
    // Replace the CASE 3.1 block in your existing waitForDonateWidget
    else if (
      (user && gatsbyLanguage !== getLangFromPath(pathname)) ||
      (!user && gatsbyLanguage === getLangFromPath(pathname))
    ) {
      const widgetContainer = document.getElementById('donate-widget');
      log({
        level: LogLevel.Debug,
        message: `waitForDonateWidget CASE 3 widgetContainer ${widgetContainer}`,
      });

      // ✅ Skip reload if widget container exists or already initialized
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-underscore-dangle
      if (!widgetContainer && !(window as any)._donateWidgetInitialized) {
        log({
          level: LogLevel.Debug,
          message: `waitForDonateWidget CASE 3.1`,
        });
        setTimeout(() => clearInterval(interval), timeoutInMs);
        window.location.href =
          origin + localizedPath(pathname, getLangFromPath(pathname) || '');
      } else {
        // Widget exists, just clear interval, no reload
        log({
          level: LogLevel.Debug,
          message: `waitForDonateWidget CASE 3.1 else`,
        });
        setTimeout(() => clearInterval(interval), timeoutInMs);
      }
    }
    // CASE 3.2
    // Otherwise no additional checks are needed.
    // We can clear interval as og widget was not found during timeoutInMs.
    else {
      log({
        level: LogLevel.Debug,
        message: `waitForDonateWidget CASE 3.2 timeoutInMs ${timeoutInMs} checkFrequencyInMs ${checkFrequencyInMs}`,
      });
      setTimeout(() => clearInterval(interval), timeoutInMs);
    }
  }, checkFrequencyInMs);
};
